import {
  ModalCWShellUI,
  OrderUI,
  RowUI,
  SelectUI,
} from '@next-order/next-order-components';
import { MenuContext } from 'contexts/MenuProvider';
import { BREAKPOINT_VALUES, MENU_ORDER_TYPE } from 'helpers/enums';
import moment from 'moment-timezone';
import { CalendarModernIcon, ClockNineIcon } from 'nexticons/outline';
import {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Swiper from 'react-id-swiper';
import { useSelector } from 'react-redux';
import Select from '../../components/Select';

function tConvert(time) {
  if (!time || time?.includes('mins')) return time;
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? ' AM' : ' PM'; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  let newTime = time.join(''); // return adjusted time or original string
  if (newTime.length === 7) newTime = '0' + newTime;

  return newTime;
}

const params = {
  slidesPerView: 'auto',
  spaceBetween: 0,
  freeMode: true,
  observer: true,
};
const params2 = {
  speed: 500,
  slidesPerView: 1,
  spaceBetween: 0,
  autoHeight: true,
  observer: true,
};
export default function ChangeTimeActionButton({
  timesArr,
  selectTime,
  type,
  defaultDateStr,
  cartState,
  setCartState,
  updateDeliveryPickupTime,
  requestSlotsRefresh,
}) {
  const { currentOrder } = useSelector((state) => state.homeReducer);
  const [open, setOpen] = useState(false);
  const [selectedDeliveryTimeId, setSelectedDeliveryTimeId] = useState(null);
  const [selectedTimeId, setSelectedTimeId] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [sliderStyle, setSliderStyle] = useState({ width: 0, left: 0 });
  const [pendingTime, setPendingTime] = useState(null);
  const activeIdxRef = useRef(0);
  const swiper1Ref = useRef(null);
  const swiperRef = useRef(null);
  const orderTypeRefs = useMemo(
    () =>
      currentOrder.orderType === '2'
        ? (cartState.deliveryTimes ?? []).map(() => createRef())
        : (cartState.pickUpTimes ?? []).map(() => createRef()),
    [currentOrder.orderType, cartState.deliveryTimes, cartState.pickUpTimes]
  );

  const { breakPoint } = useContext(MenuContext);

  const [defaultDateId, defaultTimeDisplay] = defaultDateStr?.split(' ') ?? [];
  const defaultTimeDisplayForUse = tConvert(defaultTimeDisplay);

  // An ASAP order commits `now + prep`, which rolls onto the next calendar day
  // when the store trades past midnight. Seeding the picker from that date
  // lands it on tomorrow's tab and its first clock slot (12:00 AM) even though
  // the order is ASAP — and committing that would turn the ASAP order into a
  // scheduled one. Resolve ASAP orders to the ASAP slot itself, wherever it is
  // listed. When throttling has removed the ASAP slot there is nothing to
  // resolve, so seeding falls back to the committed date.
  const asapSeed = useMemo(() => {
    if (currentOrder?.isFutureDeliveryDate) return null;
    const days = timesArr ?? [];
    for (let index = 0; index < days.length; index++) {
      const slot = days[index]?.times?.find(
        (t) => t.alternateDisplay === 'ASAP'
      );
      if (slot) return { index, dateId: days[index].dateString, slot };
    }
    return null;
  }, [timesArr, currentOrder?.isFutureDeliveryDate]);

  const snapToDay = (idx) => {
    activeIdxRef.current = idx;
    setActiveIdx(idx);
    swiperRef.current?.swiper?.slideTo(idx, 0);

    const el = orderTypeRefs[idx]?.current;
    if (el) {
      const right = el.offsetLeft + el.offsetWidth;
      swiper1Ref.current?.swiper?.setTranslate(
        right > window.innerWidth ? window.innerWidth - right - 15 : 0
      );
      setSliderStyle({ width: el.offsetWidth, left: el.offsetLeft });
    }
  };

  useEffect(() => {
    if (!open || breakPoint >= BREAKPOINT_VALUES.DESKTOP) return;

    const daysArr =
      currentOrder.orderType === '2'
        ? cartState.deliveryTimes
        : cartState.pickUpTimes;
    if (!daysArr?.length) return;

    const idx = asapSeed
      ? asapSeed.index
      : Math.max(
          0,
          daysArr.findIndex((d) => d.dateString === defaultDateId)
        );

    const daySlot = daysArr[idx];
    const timeObj =
      asapSeed?.slot ??
      daySlot?.times?.find((t) => t.displayForUse === defaultTimeDisplayForUse) ??
      daySlot?.times?.[0];
    if (timeObj) setPendingTime(timeObj);

    const rafId = requestAnimationFrame(() => snapToDay(idx));
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const dateOptions = useMemo(
    () =>
      (timesArr ?? []).map((deliveryTime) => ({
        label: deliveryTime.display,
        value: deliveryTime.dateString,
      })),
    [timesArr]
  );

  const getFilteredTimesForDate = useCallback(
    (dateId) => {
      const selectedDateSlot = timesArr?.find(
        (deliveryTime) => deliveryTime.dateString === dateId
      );
      return selectedDateSlot?.times ?? [];
    },
    [timesArr]
  );

  const timesForSelectedDate = useMemo(
    () => getFilteredTimesForDate(selectedDeliveryTimeId),
    [getFilteredTimesForDate, selectedDeliveryTimeId]
  );

  const timeOptions = useMemo(() => {
    const options = timesForSelectedDate.map((time) => ({
      label: time.display,
      value: time.displayForUse,
    }));
    // filter repeated values
    const filteredOptions = options.filter(
      (option, index, self) =>
        self.findIndex((o) => o.value === option.value) === index
    );
    return filteredOptions;
  }, [timesForSelectedDate]);

  // The time matching the currently scheduled order, falling back to the
  // first slot so a valid selection is always seeded when slots exist.
  const seedTimeId = useCallback(
    (dateId) => {
      const times = getFilteredTimesForDate(dateId);
      if (!times.length) return null;
      const hasDefault =
        dateId === defaultDateId &&
        times.some((t) => t.displayForUse === defaultTimeDisplayForUse);
      return hasDefault ? defaultTimeDisplayForUse : times[0].displayForUse;
    },
    [getFilteredTimesForDate, defaultDateId, defaultTimeDisplayForUse]
  );

  // Seed the desktop dropdowns when the modal opens so the submit button is
  // enabled without requiring the user to touch either select.
  useEffect(() => {
    if (!open || breakPoint < BREAKPOINT_VALUES.DESKTOP) return;
    const dateId = asapSeed?.dateId ?? defaultDateId;
    setSelectedDeliveryTimeId(dateId);
    setSelectedTimeId(asapSeed?.slot.displayForUse ?? seedTimeId(dateId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, breakPoint, defaultDateId, asapSeed]);

  const onSelectDeliveryTime = (value) => {
    if (value === selectedDeliveryTimeId) return;
    setSelectedDeliveryTimeId(value);
    // Immediately seed a valid time for the new date so the submit button
    // never shows a time while the underlying selection is empty.
    setSelectedTimeId(seedTimeId(value));
  };

  const onSubmit = () => {
    const time = timesForSelectedDate.find(
      (t) => t.displayForUse === selectedTimeId
    );
    if (!!time) {
      selectTime(time);
    }

    setOpen(false);
  };

  const sliderGo = (e, n) => {
    activeIdxRef.current = n;
    setActiveIdx(n);
    setSliderStyle({
      width: e.currentTarget.offsetWidth,
      left: e.currentTarget.offsetLeft,
    });
    swiperRef.current?.swiper?.slideTo(n);
  };

  // Called by each Select wheel when the user scrolls or when it inits.
  // dayIdx is the slide index the picker belongs to — reject any picker that
  // isn't the currently visible slide so parallel init callbacks don't clobber
  // each other.
  const handleChange = (timeObj, dayIdx) => {
    if (dayIdx !== activeIdxRef.current) return;
    if (!timeObj) return;
    setPendingTime(timeObj);
  };

  // "Set Time" button: commit pendingTime via updateDeliveryPickupTime.
  const setDeliveryTime = (e) => {
    e.stopPropagation();
    setCartState({ showErrorInTimeSelector: false });
    setOpen(false);
    if (pendingTime) {
      const date = moment(
        `${pendingTime.timeObjFinal} ${pendingTime.displayForUse}`,
        'YYYY-MM-DD HH:mm A'
      );
      updateDeliveryPickupTime(
        date,
        pendingTime.shiftId || '',
        false,
        0,
        pendingTime.alternateDisplay !== 'ASAP'
      );
    }
  };

  // Fires when the user swipes between day slides. Reads from the correct array
  // per order type and updates the underline indicator to match.
  const slideChange = () => {
    if (!swiperRef.current?.swiper) return;
    const activeIndex = swiperRef.current.swiper.activeIndex;
    activeIdxRef.current = activeIndex;
    setActiveIdx(activeIndex);

    const daysArr =
      currentOrder.orderType === '2'
        ? cartState.deliveryTimes
        : cartState.pickUpTimes;
    const activeDay = daysArr?.[activeIndex];
    if (!activeDay?.times?.length) return;

    setPendingTime(activeDay.times[0]);

    const el = orderTypeRefs[activeIndex]?.current;
    if (el) {
      const right = el.offsetLeft + el.offsetWidth;
      swiper1Ref.current?.swiper?.setTranslate(
        right > window.innerWidth ? window.innerWidth - right - 15 : 0
      );
      setSliderStyle({ width: el.offsetWidth, left: el.offsetLeft });
    }
  };

  const openModal = () => {
    // Ask the parent to regenerate `pickUpTimes` / `deliveryTimes` with a
    // fresh `moment()` before rendering the picker, so a user who has had
    // the page open for a while doesn't see (or pick) stale slots.
    requestSlotsRefresh?.();
    setOpen(true);
  };

  return (
    <>
      <OrderUI.ChangeBtn
        onClick={openModal}
        extendedClassName='pr-3.75'
      />
      {breakPoint >= BREAKPOINT_VALUES.DESKTOP ? (
        <ModalCWShellUI
          maxWidth={358}
          onOpenChange={() => setOpen(false)}
          open={open}
          zIndex={100}
        >
          <ModalCWShellUI.CloseButton onClick={() => setOpen(false)} />
          <ModalCWShellUI.Header title={`Select a ${type} Time`} />
          <ModalCWShellUI.Body>
            <RowUI>
              <SelectUI
                key={`date-${open}`}
                defaultValue={selectedDeliveryTimeId ?? defaultDateId}
                icon={<CalendarModernIcon width={20} />}
                options={dateOptions}
                onChange={onSelectDeliveryTime}
                styleType='cw-list'
              />
            </RowUI>
            <RowUI>
              {/* Remount the time select whenever the date changes, so the
                  displayed value always matches selectedTimeId instead of a
                  stale default. */}
              <SelectUI
                key={`time-${open}-${selectedDeliveryTimeId}`}
                defaultValue={selectedTimeId ?? undefined}
                icon={<ClockNineIcon strokeWidth={1} width={20} />}
                onChange={setSelectedTimeId}
                options={timeOptions}
                styleType='cw-list'
                disabled={!selectedDeliveryTimeId || !timeOptions.length}
              />
            </RowUI>
          </ModalCWShellUI.Body>
          <ModalCWShellUI.Footer>
            <ModalCWShellUI.Button
              onClick={onSubmit}
              disabled={!selectedDeliveryTimeId || !selectedTimeId}
            >
              Schedule {type} Time
            </ModalCWShellUI.Button>
          </ModalCWShellUI.Footer>
        </ModalCWShellUI>
      ) : (
        <ModalCWShellUI
          onOpenChange={() => setOpen(false)}
          open={open}
          zIndex={100}
        >
          <ModalCWShellUI.CloseButton onClick={() => setOpen(false)} />
          <ModalCWShellUI.Header title={`Select a ${type} Time`} />
          <ModalCWShellUI.Body extendedClassName='!px-0'>
            {currentOrder.orderType === MENU_ORDER_TYPE.pickup ? (
              <>
                <div className='swiperWeek'>
                  <Swiper {...params} ref={swiper1Ref}>
                    {cartState.pickUpTimes?.map((d, index) => (
                      <div
                        ref={orderTypeRefs[index]}
                        key={index}
                        onClick={(e) => sliderGo(e, Number(index))}
                        className={
                          activeIdx === Number(index)
                            ? 'active tabSUL'
                            : 'tabSUL'
                        }
                      >
                        {d.display}
                      </div>
                    ))}
                    <span
                      className='slidebarExtras'
                      style={{
                        width: sliderStyle.width,
                        left: sliderStyle.left,
                      }}
                    />
                  </Swiper>
                </div>
                <div className='swiperTime'>
                  <Swiper
                    {...params2}
                    on={{
                      slideChange,
                    }}
                    ref={swiperRef}
                  >
                    {cartState.pickUpTimes?.map((item, index) => (
                      <div className='subTimeXS' key={index}>
                        <Select
                          key={`picker${index}`}
                          handleChange={(timeObj) =>
                            handleChange(timeObj, index)
                          }
                          data={item}
                          index={index}
                        />
                      </div>
                    ))}
                  </Swiper>
                </div>
              </>
            ) : (
              <>
                <div className='swiperWeek'>
                  <Swiper {...params} ref={swiper1Ref}>
                    {cartState.deliveryTimes?.map((d, index) => (
                      <div
                        ref={orderTypeRefs[index]}
                        key={index}
                        onClick={(e) => sliderGo(e, Number(index))}
                        className={
                          activeIdx === Number(index)
                            ? 'active tabSUL'
                            : 'tabSUL'
                        }
                      >
                        {d.display}
                      </div>
                    ))}
                    <span
                      className='slidebarExtras'
                      style={{
                        width: sliderStyle.width,
                        left: sliderStyle.left,
                      }}
                    />
                  </Swiper>
                </div>
                <div className='swiperTime'>
                  <Swiper
                    {...params2}
                    on={{
                      slideChange,
                    }}
                    ref={swiperRef}
                  >
                    {cartState.deliveryTimes?.map((item, index) => (
                      <div className='subTimeXS' key={index}>
                        <Select
                          key={`picker${index}`}
                          handleChange={(timeObj) =>
                            handleChange(timeObj, index)
                          }
                          data={item}
                          index={index}
                        />
                      </div>
                    ))}
                  </Swiper>
                </div>
              </>
            )}
          </ModalCWShellUI.Body>
          <ModalCWShellUI.Footer>
            <ModalCWShellUI.Button onClick={(e) => setDeliveryTime(e)}>
              Set {type} Time{' '}
              {pendingTime
                ? orderTypeRefs[activeIdx]?.current &&
                  orderTypeRefs[activeIdx]?.current?.textContent !== 'Today'
                  ? ` - ${orderTypeRefs[activeIdx]?.current?.textContent}, ${
                      pendingTime.spillReadyDisplay
                        ? `~${pendingTime.spillReadyDisplay}`
                        : pendingTime.displayForUse
                    }`
                  : ` - ${
                      pendingTime.spillReadyDisplay
                        ? `~${pendingTime.spillReadyDisplay}`
                        : pendingTime.displayForUse
                    }`
                : ''}
            </ModalCWShellUI.Button>
          </ModalCWShellUI.Footer>
        </ModalCWShellUI>
      )}
    </>
  );
}
